import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Info, Mic, Play } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import episodes from "@/content/podcasty.json";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PodcastPlayer } from "@/components/PodcastPlayer";

const formatDate = (date) =>
  new Date(date).toLocaleDateString("cs-CZ", { day: "numeric", month: "long", year: "numeric" });

const PLATFORMS = [
  ["spotify", "Spotify"],
  ["apple", "Apple Podcasts"],
  ["youtube", "YouTube"],
];

const AudioBlock = ({ episode }) => {
  const [play, setPlay] = useState(false);

  const renderPlayer = () => {
    if (!episode.audioUrl) {
      return (
        <div className="border border-[#F0F0F0] bg-[#FAFAF8] p-8 md:p-10" data-testid="podcast-audio-pending">
          <div className="flex items-center gap-4">
            <Mic className="h-6 w-6 text-[#B69A58] shrink-0" strokeWidth={1.5} aria-hidden="true" />
            <p className="text-base text-[#252525]">
              Záznam epizody bude brzy k poslechu — právě se připravuje.
            </p>
          </div>
        </div>
      );
    }

    if (!play) {
      return (
        <button
          type="button"
          onClick={() => setPlay(true)}
          data-testid="podcast-play-button"
          className="w-full border border-[#F0F0F0] bg-[#FAFAF8] hover:border-[#B69A58] transition-colors duration-300 p-8 md:p-10 flex items-center gap-5 text-left"
        >
          <span className="flex items-center justify-center w-14 h-14 rounded-full bg-[#B69A58] text-white shrink-0" aria-hidden="true">
            <Play className="h-6 w-6 ml-0.5" strokeWidth={1.5} fill="currentColor" />
          </span>
          <span>
            <span className="block text-xs uppercase tracking-widest text-[#737373] font-medium">Přehrát epizodu</span>
            <span className="block text-base text-[#252525] mt-1">
              Přehrávač se načte až po kliknutí — nepředáváme vaše data třetím stranám bez vašeho svolení.
            </span>
          </span>
        </button>
      );
    }

    return (
      <div data-testid="podcast-player">
        {episode.audioUrl.startsWith("/") ? (
          <PodcastPlayer src={episode.audioUrl} />
        ) : (
          <iframe
            src={episode.audioUrl}
            title={`Přehrávač epizody: ${episode.title}`}
            width="100%"
            height="152"
            loading="lazy"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            className="border-0 block"
          />
        )}
      </div>
    );
  };

  return (
    <div className="mt-10">
      {renderPlayer()}
      {episode.aiGenerated && <AiNotice />}
    </div>
  );
};

const AiNotice = () => (
  <div
    className="mt-4 border border-[#F0F0F0] bg-[#FAFAF8] p-4 md:p-5 flex gap-3"
    data-testid="podcast-ai-notice"
    role="note"
    aria-label="Informace o vzniku podcastu"
  >
    <Info className="h-4 w-4 text-[#B69A58] shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
    <div className="text-xs md:text-sm leading-relaxed text-[#4A4A4A]">
      <p className="font-medium text-[#252525]">Informace o vzniku podcastu</p>
      <p className="mt-1">
        „Tato epizoda byla vytvořena s využitím umělé inteligence. Hlasy moderátorů jsou synteticky generované a
        nepředstavují skutečné osoby.
      </p>
      <p className="mt-1">
        Podcast má vzdělávací a informační charakter. Obsah nenahrazuje individuální finanční poradenství.“
      </p>
    </div>
  </div>
);

export default function PodcastyDetail() {
  const { slug } = useParams();
  const episode = episodes.find((e) => e.slug === slug);
  const episodeNumber = episodes.findIndex((e) => e.slug === slug) + 1;
  const platformLinks = episode ? PLATFORMS.filter(([key]) => episode.links?.[key]) : [];

  useEffect(() => {
    if (episode) {
      document.title = `${episode.title} | Podcast Daniel Pekař`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", episode.excerpt);
    }
    return () => { document.title = "Daniel Pekař – finanční poradce Ústí nad Labem"; };
  }, [episode]);

  return (
    <>
      <Navbar />
      <main className="pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {!episode ? (
            <div data-testid="podcast-not-found">
              <h1 className="font-display text-4xl text-[#252525]">Tuto epizodu jsem nenašel</h1>
              <Link
                to="/podcasty"
                data-testid="podcast-back-overview"
                className="inline-flex items-center gap-2 mt-8 text-sm text-[#252525] border-b border-[#B69A58] pb-1 hover:text-[#B69A58] transition-colors duration-300"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                Všechny epizody
              </Link>
            </div>
          ) : (
            <article data-testid="podcast-detail">
              <Link
                to="/podcasty"
                data-testid="podcast-back-link"
                className="inline-flex items-center gap-2 text-sm text-[#252525] border-b border-[#B69A58] pb-1 hover:text-[#B69A58] transition-colors duration-300"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                Podcast
              </Link>

              <p className="text-xs uppercase tracking-widest text-[#B69A58] font-medium mt-10">
                Epizoda {episodeNumber}
                <span className="text-[#737373] font-normal">
                  {" · "}{formatDate(episode.date)}{episode.duration ? ` · ${episode.duration}` : ""}
                </span>
              </p>
              <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.12] text-[#252525] mt-6">
                {episode.title}
              </h1>
              <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed mt-8">{episode.excerpt}</p>

              {episode.image && (
                <img
                  src={episode.image}
                  alt={`Obálka epizody — ${episode.title}`}
                  width={840}
                  height={560}
                  loading="lazy"
                  data-testid="podcast-cover"
                  className="mt-10 w-full aspect-[3/2] object-cover object-[50%_30%] block"
                />
              )}

              <AudioBlock episode={episode} />

              {platformLinks.length > 0 && (
                <div className="mt-8 flex flex-wrap items-center gap-4" data-testid="podcast-platforms">
                  <span className="text-xs uppercase tracking-widest text-[#737373] font-medium">Poslouchejte na</span>
                  {platformLinks.map(([key, label]) => (
                    <a
                      key={key}
                      href={episode.links[key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`podcast-platform-${key}`}
                      className="text-sm text-[#252525] border-b border-[#B69A58] pb-1 hover:text-[#B69A58] transition-colors duration-300"
                    >
                      {label}
                    </a>
                  ))}
                </div>
              )}

              {episode.notes?.length > 0 && (
                <>
                  <h2 className="font-display text-2xl md:text-3xl text-[#252525] mt-14">Poznámky k epizodě</h2>
                  <ul className="mt-6 space-y-4">
                    {episode.notes.map((note, i) => (
                      <li key={i} className="flex gap-4">
                        <span className="w-4 h-[1px] bg-[#B69A58] shrink-0 mt-3" aria-hidden="true" />
                        <p className="text-base text-[#4A4A4A] leading-relaxed">{note}</p>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {episode.transcript?.length > 0 && (
                <Accordion type="single" collapsible className="mt-14 border-t border-b border-[#F0F0F0]">
                  <AccordionItem value="transcript" className="border-b-0 py-2">
                    <AccordionTrigger
                      data-testid="podcast-transcript-trigger"
                      className="text-lg md:text-xl font-medium text-[#252525] hover:text-[#B69A58] hover:no-underline transition-colors duration-300 text-left font-display"
                    >
                      Přepis epizody
                    </AccordionTrigger>
                    <AccordionContent data-testid="podcast-transcript-content">
                      <div className="space-y-4 pr-8">
                        {episode.transcript.map((paragraph, i) => (
                          <p key={i} className="text-base text-[#4A4A4A] leading-relaxed">{paragraph}</p>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}

              <div className="mt-14 border border-[#F0F0F0] p-8 md:p-10" data-testid="podcast-cta">
                <h2 className="font-display text-2xl text-[#252525]">Chcete to probrat osobně?</h2>
                <p className="text-base text-[#4A4A4A] leading-relaxed mt-3">
                  Ozvěte se mi — domluvíme nezávaznou konzultaci, osobně v Ústí nad Labem nebo online.
                </p>
                <Link
                  to="/#kontakt"
                  data-testid="podcast-consultation-link"
                  className="mt-8 inline-flex items-center justify-center border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-widest font-medium"
                >
                  Domluvit konzultaci
                </Link>
              </div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
